// History: every published version, newest first, and "Roll back" — which
// republishes an old version as a new one (the draft follows it).

import { useEffect, useState } from 'react'
import { RotateCcw } from 'lucide-react'
import { KitModal } from '../../designerKit/KitModal'
import { useKitConfirm } from '../../designerKit/KitModal'
import type { UdaHistoryEntry } from '../types'
import { useSetup } from './state'
import { Banner } from './ui'

export default function HistoryDialog({ onClose }: { onClose: () => void }) {
  const { transport, objName, draft, notify, catalog } = useSetup()
  const { confirm, confirmDialog } = useKitConfirm(catalog)
  const [list, setList] = useState<UdaHistoryEntry[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  useEffect(() => {
    let cancelled = false
    transport.history(objName)
      .then(r => { if (!cancelled) setList([...(r.versions || [])].sort((a, b) => b.version - a.version)) })
      .catch(err => {
        console.warn('[uda setup] history failed', err)
        if (!cancelled) setError((err as Error)?.message || 'The history could not be read')
      })
    return () => { cancelled = true }
  }, [transport, objName])
  const rollback = async (v: number) => {
    if (draft.pending) await draft.flush()
    if (!(await confirm({ title: `Roll back to version ${v}?`, tone: 'primary', confirmLabel: 'Roll back',
      message: `Version ${v} is published again as a new version, and your draft becomes it. Values already saved are kept.` }))) return
    setBusy(v)
    try {
      const r = await transport.rollback(objName, v)
      await draft.reload()
      notify('success', `Version ${v} is published again as version ${r.version}.`)
      onClose()
    } catch (err) {
      console.warn('[uda setup] rollback failed', err)
      const e = err as { message?: string; detail?: { message?: string; details?: { errors?: string[] } } }
      setError([e?.detail?.message || e?.message || 'Rolling back failed', ...(e?.detail?.details?.errors || [])].join(' — '))
    } finally {
      setBusy(null)
    }
  }
  return (
    <KitModal open onClose={onClose} title="Published versions"
      footer={<button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Close</button>}>
      <div className="uds-dlg" style={{ display: 'grid', gap: 8 }}>
        {error && <Banner tone="e">{error}</Banner>}
        {!list && !error && <div className="uds-hint"><span className="uds-spin" /> Reading…</div>}
        {list && !list.length && <div className="uds-na">Nothing has been published yet.</div>}
        {(list || []).map((h, i) => (
          <div key={h.version} className="uds-li" style={{ marginTop: 0 }}>
            <span className="t">
              <b>Version {h.version}</b>{i === 0 ? ' · live' : ''}{h.summary ? ` · ${h.summary}` : ''}
              <span style={{ color: 'var(--color-text-faint)' }}>
                {h.published_at ? ` · ${new Date(h.published_at).toLocaleString()}` : ''}{h.published_by ? ` · ${h.published_by}` : ''}
              </span>
            </span>
            {i > 0 && (
              <button type="button" className="btn btn-secondary btn-sm" disabled={busy !== null} onClick={() => { void rollback(h.version) }}>
                {busy === h.version ? <span className="uds-spin" /> : <RotateCcw size={14} />} Roll back
              </button>
            )}
          </div>
        ))}
      </div>
      {confirmDialog}
    </KitModal>
  )
}
